<script>
// 静态语言数据，放在普通 script 标签中以避免 setup 提升问题
const LANGUAGES_DATA = [
  { region: 'Global', language: 'English', url: 'https://www.tendacn.com' },
  { region: '中国', language: '简体中文', url: '' },
  { region: 'United States', language: 'English · us', url: 'https://www.tendacn.com/us/' },
  { region: 'Peru', language: 'Español · pe', url: 'https://www.tendacn.com/pe/' },
  { region: 'Mexico', language: 'Español · mx', url: 'https://www.tendacn.com/mx/' },
  { region: 'Colombia', language: 'Español · co', url: 'https://www.tendacn.com/co/' },
  { region: 'Brazil', language: 'Português · br', url: 'https://www.tendacn.com/br/' },
  { region: 'Argentina', language: 'Español · ar', url: 'https://www.tendacn.com/ar/' },
  { region: 'Vietnam', language: 'Tiếng Việt Nam · vn', url: 'https://www.tendacn.com/vn/' },
  { region: 'Thailand', language: 'ไทย · th', url: 'https://www.tendacn.com/th/' },
  { region: 'Singapore', language: 'English · sg', url: 'https://www.tendacn.com/sg/' },
  { region: 'Philippines', language: 'English · ph', url: 'https://www.tendacn.com/ph/' },
  { region: 'Malaysia', language: 'English · my', url: 'https://www.tendacn.com/my/' },
  { region: 'Indonesia', language: 'English · id', url: 'https://www.tendacn.com/id/' },
  { region: 'India', language: 'English · in', url: 'https://www.tendacn.com/in/' },
  { region: 'Australia', language: 'English · au', url: 'https://www.tendacn.com/au/' },
  { region: 'Uzbekistan', language: 'Русский · uz', url: 'https://www.tendacn.com/uz/' },
  { region: 'United Kingdom', language: 'English · uk', url: 'https://www.tendacn.com/uk/' },
  { region: 'Ukraine', language: 'Українська · ua', url: 'https://www.tendacn.com/ua/' },
  { region: 'Turkey', language: 'Türkçe · tr', url: 'https://www.tendacn.com/tr/' },
  { region: 'Spain', language: 'español · es', url: 'https://www.tendacn.com/es/' },
  { region: 'Russia', language: 'Русский · ru', url: 'https://www.tendacn.com/ru/' },
  { region: 'Romania', language: 'Română · ro', url: 'https://www.tendacn.com/ro/' },
  { region: 'Poland', language: 'Polski · pl', url: 'https://www.tendacn.com/pl/' },
  { region: 'Italia', language: 'Italiano · it', url: 'https://www.tendacn.com/it/' },
  { region: 'Germany', language: 'Deutsch · de', url: 'https://www.tendacn.com/de/' },
  { region: 'France', language: 'Français · fr', url: 'https://www.tendacn.com/fr/' },
  { region: 'Kazakhstan', language: 'English · kz', url: 'https://www.tendacn.com/kz/' },
  {
    region: 'United Arab Emirates',
    language: 'English · ae',
    url: 'https://www.tendacn.com/ae/',
  },
  { region: 'South Africa', language: 'English · za', url: 'https://www.tendacn.com/za/' },
  { region: 'Egypt', language: 'English · eg', url: 'https://www.tendacn.com/eg/' },
  { region: 'Saudi Arabia', language: 'English · sa', url: 'https://www.tendacn.com/sa/' },
  { region: 'Iraq', language: 'English · iq', url: 'https://www.tendacn.com/iq/' },
  { region: 'Kenya', language: 'English · ke', url: 'https://www.tendacn.com/ke/' },
]
</script>

<script setup>
/**
 * @description 语言选择面板组件，包含搜索和语言列表
 * @param {Array} languages - 语言数据列表
 * @param {Object} languages[].region - 地区名称
 * @param {Object} languages[].language - 语言名称
 * @param {Object} languages[].url - 跳转链接
 * @param {string} currentLanguage - 当前选中的语言名称
 * @emits select - 当选择某种语言时触发
 * @emits close - 当点击关闭按钮时触发
 */

// 1. Imports
import { ref, computed } from 'vue'
import { IconClose, IconCheck } from '@arco-design/web-vue/es/icon'

// 2. Props
const props = defineProps({
  languages: {
    type: Array,
    default: () => LANGUAGES_DATA,
  },
  currentLanguage: {
    type: String,
    default: '中国 - 简体中文',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
})

// 3. Emits
const emit = defineEmits(['select', 'close', 'focus', 'blur', 'pointerdown'])

// 4. Refs/Reactive State
const searchQuery = ref('')

// 5. Computed
const filteredLanguages = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return props.languages
  return props.languages.filter(
    (lang) =>
      lang.region.toLowerCase().includes(query) || lang.language.toLowerCase().includes(query)
  )
})

// 6. Methods
const handleSelect = (lang) => {
  emit('select', lang)
}

const handleClose = () => {
  emit('close')
}

const handlePanelPointerDown = () => {
  emit('pointerdown')
}

const handleInputFocus = () => {
  emit('focus')
}

const handleInputBlur = () => {
  emit('blur')
}

// 7. Reset Search (Public method if needed)
defineExpose({
  resetSearch: () => {
    searchQuery.value = ''
  },
})
</script>

<template>
  <div class="language-panel" @mousedown.capture="handlePanelPointerDown">
    <div class="language-panel__header">
      <div class="language-panel__current">
        <icon-check class="language-panel__check-icon" />
        <span>{{ currentLanguage }}</span>
      </div>
      <icon-close v-if="showClose" class="language-panel__close-icon" @click="handleClose" />
    </div>

    <div class="language-panel__search">
      <a-input-search
        v-model="searchQuery"
        class="language-panel__search-input"
        placeholder="搜索"
        allow-clear
        @focus="handleInputFocus"
        @blur="handleInputBlur"
      />
    </div>

    <div class="language-panel__list">
      <div
        v-for="lang in filteredLanguages"
        :key="lang.url"
        class="language-panel__item"
        :class="{
          'language-panel__item--active': `${lang.region} - ${lang.language}` === currentLanguage,
        }"
      >
        <NuxtLink
          v-if="lang.url"
          :to="lang.url"
          class="language-panel__link"
          @click="handleSelect(lang)"
        >
          <div class="language-panel__item-content">
            <span class="language-panel__region-text">
              {{ lang.region }} -
              <span class="language-panel__language">{{ lang.language }}</span>
            </span>
          </div>
        </NuxtLink>
        <div v-else class="language-panel__text" @click="handleSelect(lang)">
          <div class="language-panel__item-content">
            <span class="language-panel__region-text">
              {{ lang.region }} -
              <span class="language-panel__language">{{ lang.language }}</span>
            </span>
          </div>
        </div>
      </div>
      <div v-if="filteredLanguages.length === 0" class="language-panel__empty">未找到相关语言</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.language-panel {
  width: 100%;
  max-height: 624px;
  background-color: $bg-white;
  display: flex;
  flex-direction: column;
  border-radius: $radius-sm;
  padding: 16px 20px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #d9d9d9;
    color: $text-title-color;
    font-size: 14px;
    line-height: 22px;
    padding-bottom: 12px;
  }

  &__current {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: 600;
    color: $text-title-color;
    gap: $spacing-xs;
  }

  &__check-icon {
    color: $primary-color;
    font-size: 16px;
  }

  &__close-icon {
    cursor: pointer;
    font-size: 16px;
    color: $text-subtitle-color;
    transition: color 0.2s;

    &:hover {
      color: $text-title-color;
    }
  }
  &__search {
    margin-top: 12px;
  }

  &__list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    margin-top: 16px;
    padding-right: 4px;
    -webkit-overflow-scrolling: touch;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__item {
    transition: background-color 0.2s;
    font-size: 12px;
    line-height: 20px;
    margin-top: 0;
    padding: 0 12px;

    & + & {
      margin-top: 16px;
    }

    &:hover {
      .language-panel__link,
      .language-panel__text,
      .language-panel__language,
      .language-panel__region-text {
        color: $primary-color;
        text-decoration: underline;
        text-decoration-color: currentColor;
      }
    }
  }

  &__link,
  &__text {
    display: block;
    font-size: 12px;
    color: $text-title-color;
    text-decoration: none;
    cursor: pointer;
    width: 100%;
    transition: all 0.2s;
  }

  &__item-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__active-icon {
    color: $primary-color;
    font-size: 16px;
  }

  &__language {
    color: $text-content-color;
    transition: color 0.2s;
  }

  &__empty {
    padding: $spacing-lg $spacing-lg 0;
    text-align: center;
    color: $text-subtitle-color;
    font-size: $font-size-card-content;
  }
}

@include respond-to-max('lg') {
  .language-panel {
    max-height: none;
    padding: 0;
    border-radius: 0;

    &__header {
      display: none; // 在 ActionSheet 中通常不需要自己的头部
    }

    &__search {
      margin-top: 0;
      padding: 12px 16px;
      position: sticky;
      top: 0;
      background-color: $bg-white;
      z-index: 1;
    }

    &__search-input {
      height: $tool-topbar-height;

      :deep(.arco-input-wrapper),
      :deep(.arco-input) {
        height: $tool-topbar-height;
      }

      :deep(.arco-input-wrapper) {
        border: none;
        border-radius: $radius-6;
        background-color: $bg-color;
        padding: 0 $spacing-sm;
      }

      :deep(.arco-input),
      :deep(.arco-input::placeholder) {
        font-size: $font-size-card-content;
      }

      :deep(.arco-input-search-icon) {
        color: $text-content-color;
        font-size: $font-size-card-subtitle;
      }
    }

    &__list {
      margin-top: 0;
      padding: 0 16px 24px;
      overflow-y: auto;
      max-height: 60vh;
      -webkit-overflow-scrolling: touch;
    }

    &__item {
      margin-top: 0;
      padding: 0;
      border-bottom: 1px solid $border-dashed-color;
      & + & {
        margin-top: 0;
      }
    }

    &__link,
    &__text {
      display: flex;
      align-items: center;
      height: $base-header-height-mobile;
      padding: 0 $spacing-2xs;
      font-size: 16px;
    }
  }
}
</style>
